"use client";

import { Button } from "@/components/builder/button";
import Spinner from "@/components/spinner";
import { env } from "@/env.mjs";
import { useLocale } from "@/libs/base/components/locale-provider";
import { MimeTypes } from "@/libs/base/constants/mime-types";
import { ValidationErrors } from "@/libs/base/models/validation";
import { cn } from "@/libs/base/utils/layout";
import ValidationUtils from "@/libs/base/utils/validation-utils";
import {
  SubscribeNewsletterContract,
  newsletterEmailSchema,
  newsletterNameSchema,
  validNewsletterPronouns,
} from "@/models/newsletter";
import { Turnstile } from "@marsidev/react-turnstile";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { NewsletterTranslations } from "../layout/footer/translations";

const Steps = {
  EMAIL: 0,
  NAME: 1,
  SUCCESS: 2,
} as const;
type Step = (typeof Steps)[keyof typeof Steps];

interface NewsletterProps {
  translations: NewsletterTranslations;
  square?: boolean;
}

export default function Newsletter(props: NewsletterProps) {
  const locale = useLocale();
  const [step, setStep] = useState<Step>(Steps.EMAIL);

  const [turnstileToken, setTurnstileToken] = useState<string>();

  const [formData, setFormData] = useState<SubscribeNewsletterContract>({
    email: "",
    firstName: "",
    lastName: "",
    pronoun: "",
    language: locale,
  }); // TODO add csrf token?
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  async function handleSubmitFromStep0(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const validationResult = ValidationUtils.validate(newsletterEmailSchema, formData);
    if (validationResult.errors) {
      setValidationErrors(validationResult.errors);
      return;
    }

    setFormData({ ...formData, email: validationResult.data!.email });
    setStep(Steps.NAME);
  }

  async function handleSubmitFromStep1(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const validationResult = ValidationUtils.validate(newsletterNameSchema, formData);
    if (validationResult.errors) {
      setValidationErrors(validationResult.errors);
      return;
    }

    setFormData({
      ...formData,
      firstName: validationResult.data!.firstName,
      lastName: validationResult.data!.lastName,
      pronoun: validationResult.data!.pronoun,
    });

    setIsFormSubmitted(true);
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear the error for the corresponding field when the user starts typing
    removeValidationError(name);
  };

  function removeValidationError(fieldToRemove: string) {
    if (!validationErrors[fieldToRemove]) {
      return;
    }

    setValidationErrors((errors) => {
      const { [fieldToRemove]: _, ...updatedValidationErrors } = errors;
      return updatedValidationErrors;
    });
  }

  useEffect(() => {
    if (!isFormSubmitted) {
      return;
    }

    // FIXME: missing CSRF
    fetch("/api/newsletters/subscribe", {
      method: "POST",
      headers: {
        "Content-Type": MimeTypes.APPLICATION_JSON,
      },
      body: JSON.stringify({ ...formData, cfTurnstileToken: turnstileToken }),
    })
      .then(async (response) => {
        if (response.status === 200) {
          setStep(Steps.SUCCESS);
        } else {
          setError(
            `Unexpected error occurred. Please try again later or call the support if persists. Status: ${response.status} ${response.statusText}`,
          );
        }
      })
      .finally(() => setIsFormSubmitted(false));
  }, [isFormSubmitted]);

  if (isFormSubmitted) {
    return <Spinner size={10} className="mx-auto mt-8" />;
  }

  if (step === Steps.EMAIL) {
    return (
      <NewsletterStep0
        onSubmit={handleSubmitFromStep0}
        formData={formData}
        validationErrors={validationErrors}
        error={error}
        onChange={handleChange}
        translations={props.translations}
        locale={locale}
        square={props.square}
      />
    );
  }

  if (step === Steps.NAME) {
    return (
      <NewsletterStep1
        onSuccessTurnstileToken={setTurnstileToken}
        turnstileToken={turnstileToken}
        onSubmit={handleSubmitFromStep1}
        formData={formData}
        validationErrors={validationErrors}
        error={error}
        onChange={handleChange}
        translations={props.translations}
        locale={locale}
        square={props.square}
      />
    );
  }

  if (step === Steps.SUCCESS) {
    return (
      <div>
        <h1>{props.translations.success_message}</h1>
      </div>
    );
  }
}

interface SubComponentProps {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  formData: SubscribeNewsletterContract;
  validationErrors: ValidationErrors;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  error: string | null;
  translations: NewsletterTranslations;
  locale: string;
  square?: boolean;
  turnstileToken?: string;
  onSuccessTurnstileToken?: Dispatch<SetStateAction<string | undefined>>;
}

function NewsletterStep0(props: SubComponentProps) {
  return (
    <form
      onSubmit={props.onSubmit}
      className={cn("flex h-full flex-col", props.square && "max-sm:aspect-1 max-sm:justify-between")}
    >
      <div className="text-4xl font-light">{props.translations.step_1_heading}</div>

      <div className="mt-32 flex flex-col gap-3 lg:mt-auto">
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          aria-label={props.translations.step_1_mail_placeholder}
          placeholder={props.translations.step_1_mail_placeholder}
          className={cn(
            "placeholder:lightgray-100 w-full border-[1px] border-black p-2.5 text-xs font-light text-black outline-none",
            props.validationErrors["email"] && "ring-red-300",
          )}
          value={props.formData.email}
          onChange={props.onChange}
        />
        {props.validationErrors["email"] && (
          <ul className="mt-2 text-sm text-red-500">
            {props.validationErrors["email"].map((error: string) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
        <Button type="submit" hasArrow text={props.translations.step_1_next} />
      </div>
    </form>
  );
}

function NewsletterStep1(props: SubComponentProps) {
  // TODO: Move to translations
  const pronounMapping: Record<string, string> = {
    "he/him": props.translations.step_2_pronoun_he,
    "she/her": props.translations.step_2_pronoun_she,
    "they/them": props.translations.step_2_pronoun_they,
  };

  return (
    <form
      onSubmit={props.onSubmit}
      className={cn("flex h-full flex-col gap-3.5", props.square && "max-sm:aspect-1 max-sm:justify-between")}
    >
      <div className="mb-4 text-4xl font-light">{props.translations.step_2_heading}</div>
      <div className="flex flex-row gap-3">
        <input
          id="firstName"
          name="firstName"
          type="text"
          autoComplete="firstName"
          required
          aria-label={props.translations.step_2_first_name_placeholder}
          placeholder={props.translations.step_2_first_name_placeholder}
          className={cn(
            "placeholder:lightgray-100 w-full p-2.5 text-sm font-semibold leading-6 text-black",
            "border-[1px] border-black outline-none",
            props.validationErrors["firstName"] ? "ring-red-300" : "ring-gray-300",
          )}
          value={props.formData.firstName}
          onChange={props.onChange}
        />
        {props.validationErrors["firstName"] && (
          <ul className="mt-2 text-sm text-red-500">
            {props.validationErrors["firstName"].map((error: string) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
        <input
          id="lastName"
          name="lastName"
          type="text"
          autoComplete="lastName"
          required
          aria-label={props.translations.step_2_last_name_placeholder}
          placeholder={props.translations.step_2_last_name_placeholder}
          className={cn(
            "placeholder:lightgray-100 w-full p-2.5 text-sm font-semibold leading-6 text-black",
            "border-[1px] border-black outline-none",
            props.validationErrors["lastName"] ? "ring-red-300" : "ring-gray-300",
          )}
          value={props.formData.lastName}
          onChange={props.onChange}
        />
        {props.validationErrors["lastName"] && (
          <ul className="mt-2 text-sm text-red-500">
            {props.validationErrors["lastName"].map((error: string) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <legend>{props.translations.step_2_pronouns}</legend>
        <div className="flex flex-row justify-between">
          {validNewsletterPronouns.map((pronoun, index) => (
            <div key={pronoun}>
              <input
                id={`pronoun-${index}`}
                name="pronoun"
                type="radio"
                required
                className={cn(
                  "checked:border-transparent checked:bg-black checked:ring-black",
                  props.validationErrors["pronoun"] ? "ring-red-300" : "ring-gray-300",
                )}
                value={pronoun}
                checked={props.formData.pronoun === pronoun}
                onChange={props.onChange}
              />
              <label htmlFor="pronoun" className={cn("ml-1 font-light", props.locale === "de" ? "text-sm" : "text-xs")}>
                {pronounMapping[pronoun]}
              </label>
            </div>
          ))}
        </div>
        {props.validationErrors["pronoun"] && (
          <ul className="mt-2 text-sm text-red-500">
            {props.validationErrors["pronoun"].map((error: string) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-9 flex flex-col gap-2 xl:mt-auto">
        <p className="text-center text-[10px] text-lightgray-300">{props.translations.step_2_privacy_policy}</p>

        <button
          type="submit"
          className="flex w-full justify-center rounded-full bg-black px-3 py-1.5 text-sm leading-6 text-white transition-all duration-500 ease-in-out hover:bg-white hover:text-black"
          aria-disabled={props.turnstileToken ? false : true}
        >
          {props.translations.step_2_sign_up}
        </button>
        {props.error && <div className="text-sm text-red-800">{props.error}</div>}
      </div>
      <Turnstile siteKey={env.NEXT_PUBLIC_TURNSTILE_SITE_KEY} onSuccess={props.onSuccessTurnstileToken} />
    </form>
  );
}
